import axios from 'axios';
import { installLibraryQueryCache, invalidateLibraryMutation } from './libraryQueryCache';
import { appendCategoryFilters, categoryQuery } from './categoryFilters';
import {
  GENERIC_SERVER_ERROR_MESSAGE,
  getSupportInstructionMessage,
} from '../constants/support';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const MCP_API_URL = process.env.REACT_APP_MCP_API_URL || API_URL;
const MCP_OAUTH_API_URL = MCP_API_URL.replace(/\/mcp\/?$/, '');

/** Dispatched on 401 so AuthProvider can clear state and redirect to login. */
export const AUTH_SESSION_EXPIRED_EVENT = 'prompthub:auth-session-expired';

let authSessionExpiredEmitting = false;

/** 401 on these routes is expected (wrong password, etc.) — do not force global logout. */
const AUTH_401_EXCLUDE_PATH_PREFIXES = [
  '/auth/login',
  '/auth/signup',
  '/auth/register',
  '/auth/verify-email',
  '/auth/resend-verification',
  '/auth/forgot-password',
  '/auth/confirm-password-reset',
];

function shouldEmitSessionExpiredFor401(config) {
  if (!config?.url) return true;
  let path = config.url.split('?')[0];
  if (path.startsWith('http')) {
    try {
      path = new URL(path).pathname;
    } catch {
      return true;
    }
  }
  return !AUTH_401_EXCLUDE_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
}

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});
installLibraryQueryCache(api, () => localStorage.getItem('token'));

export const getPublicTrialOffer = async () => {
  try {
    const response = await axios.get(`${API_URL}/public/trial-offer`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// Add request interceptor to include auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Helper function to extract error message from error object
const getErrorMessage = (error) => {
  if (error?.response?.data) {
    const data = error.response.data;
    // If data is a string, return it
    if (typeof data === 'string') {
      return data;
    }
    // If data is an object with a message property, return it
    if (data?.message) {
      if (data.message === GENERIC_SERVER_ERROR_MESSAGE) {
        return getSupportInstructionMessage(Boolean(localStorage.getItem('token')));
      }
      return data.message;
    }
    // If data is an object, try to stringify it nicely
    if (typeof data === 'object') {
      return JSON.stringify(data);
    }
  }
  if (error?.message === GENERIC_SERVER_ERROR_MESSAGE) {
    return getSupportInstructionMessage(Boolean(localStorage.getItem('token')));
  }
  // Fall back to error.message or a default message
  return error?.message || 'An error occurred';
};

export const emitSessionDataInvalidation = (eventName) => {
  try {
    window.dispatchEvent(new CustomEvent(eventName));
  } catch {
    // Ignore environments without window or CustomEvent support.
  }
};

/** Sync interaction lists in memory without refetch (avoids library-wide loading flashes). Detail: { key, id, add } */
export const OPTIMISTIC_INTERACTION_EVENT = 'prompthub:optimistic-interaction';

function emitInteractionOptimistic(detail) {
  try {
    window.dispatchEvent(new CustomEvent(OPTIMISTIC_INTERACTION_EVENT, { detail }));
  } catch {
    // Ignore environments without window or CustomEvent support.
  }
}

/** Coalesce concurrent /auth/profile calls (e.g. AuthProvider checkAuth + SessionDataProvider getProfile). */
let getUserProfileInFlight = null;

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    const { method, url = '' } = response.config || {};
    invalidateLibraryMutation(method, url);
    if (['post', 'put', 'patch'].includes(method) && (/^\/prompts(?:\/|$)/.test(url) || url === '/auth/profile' || url.includes('invite'))) {
      window.dispatchEvent(new Event('promptcrew:activation-changed'));
    }
    return response;
  },
  (error) => {
    if (
      error?.response?.status === 401 &&
      error.config &&
      shouldEmitSessionExpiredFor401(error.config) &&
      !authSessionExpiredEmitting
    ) {
      authSessionExpiredEmitting = true;
      try {
        window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT));
      } catch (e) {
        authSessionExpiredEmitting = false;
      }
    }
    console.error('API Error:', getErrorMessage(error));
    return Promise.reject(error);
  }
);

export const getPrompt = async (id) => {
  try {
    const response = await api.get(`/prompts/${id}`, {
      params: { _t: Date.now() }
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const completeMcpAuthorization = async (requestId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${MCP_OAUTH_API_URL}/oauth/authorize/complete`,
      { request_id: requestId },
      {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const listPrompts = async (filters = {}) => {
  try {
    const response = await api.get('/prompts', { 
      params: { 
        ...categoryQuery(filters),
        _t: Date.now() // Cache busting
      } 
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const listTeamPrompts = async (filters = {}) => {
  try {
    const response = await api.get('/prompts', { params: { ...categoryQuery(filters), scope: 'team' } });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const listCompanyPrompts = async (filters = {}) => {
  try {
    const response = await api.get('/prompts', { params: { ...categoryQuery(filters), scope: 'company' } });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const listSharedPrompts = async (filters = {}) => {
  try {
    // Use 'shared' scope to get prompts with permission inheritance
    // This will show prompts the user has access to via direct permissions, team, or group membership
    const response = await api.get('/prompts', { params: { ...categoryQuery(filters), scope: 'shared' } });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const listUsedPrompts = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    appendCategoryFilters(params, filters);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.cursor) params.append('cursor', filters.cursor);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
    if (filters.communityOnly) params.append('communityOnly', 'true');
    if (filters.summaryOnly) params.append('summaryOnly', 'true');
    
    const response = await api.get(`/prompts/used?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const listFavoritePrompts = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    appendCategoryFilters(params, filters);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.cursor) params.append('cursor', filters.cursor);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
    if (filters.communityOnly) params.append('communityOnly', 'true');
    
    const response = await api.get(`/prompts/favorites?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const listCommunityPrompts = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    params.append('scope', 'community'); // Use scope=community instead of /prompts/community endpoint
    if (filters.search) params.append('search', filters.search);
    if (filters.categories && Array.isArray(filters.categories) && filters.categories.length > 0) {
      params.append('categories', filters.categories.join(','));
    } else if (filters.category && filters.category !== 'all') {
      params.append('category', filters.category);
    }
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.cursor) params.append('cursor', filters.cursor);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
    
    const response = await api.get(`/prompts?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const listUserPrompts = async (filters = {}) => {
  try {
    const response = await api.get('/prompts/user', { 
      params: { 
        ...categoryQuery(filters),
        _t: Date.now() // Cache busting
      } 
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const listDrafts = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    appendCategoryFilters(params, filters);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.cursor) params.append('cursor', filters.cursor);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
    
    const response = await api.get(`/prompts/drafts?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const listPromptsByCollection = async (collectionId) => {
  try {
    const response = await api.get(`/prompts/collection/${collectionId}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const createPrompt = async (promptData) => {
  try {
    const response = await api.post('/prompts', promptData);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const provisionStarterPack = async (topics = []) => {
  try {
    const response = await api.post('/starter-pack/provision', { topics });
    window.dispatchEvent(new Event('promptcrew:library-changed'));
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const updatePrompt = async (id, promptData) => {
  try {
    const response = await api.put(`/prompts/${id}`, promptData);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const deletePrompt = async (id) => {
  try {
    const response = await api.delete(`/prompts/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const getPromptVersions = async (promptId) => {
  try {
    const response = await api.get(`/prompts/${promptId}/versions`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const restorePromptVersion = async (promptId, versionId) => {
  try {
    const response = await api.post(`/prompts/${promptId}/versions/${versionId}/restore`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const createComment = async (promptId, content, versionId = null) => {
  try {
    const response = await api.post(`/prompts/${promptId}/comments`, {
      content,
      versionId
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const getPromptComments = async (promptId, versionId = null) => {
  try {
    const params = versionId ? { versionId } : {};
    const response = await api.get(`/prompts/${promptId}/comments`, { params });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const getTemplates = async (filters = {}) => {
  try {
    const response = await api.get('/templates', { params: filters });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// Unified search function that searches across prompts, collections, and contexts
export const searchAll = async (searchTerm, scope = 'shared') => {
  try {
    if (!searchTerm || !searchTerm.trim()) {
      return {
        prompts: [],
        collections: [],
        contexts: [],
        total: 0
      };
    }

    // Search all three types in parallel
    const [promptsResult, collectionsResult, contextsResult] = await Promise.all([
      listPrompts({ search: searchTerm.trim(), scope }),
      listCollections(scope, { search: searchTerm.trim() }),
      listContexts(scope, { search: searchTerm.trim() })
    ]);

    return {
      prompts: promptsResult.prompts || [],
      collections: collectionsResult.collections || [],
      contexts: contextsResult.contexts || [],
      total: (promptsResult.prompts?.length || 0) + 
             (collectionsResult.collections?.length || 0) + 
             (contextsResult.contexts?.length || 0)
    };
  } catch (error) {
    console.error('Error in unified search:', error);
    throw error.response?.data || error.message;
  }
};

export const listCollections = async (scope = 'user', filters = {}) => {
  try {
    const params = new URLSearchParams();
    params.append('scope', scope);
    if (filters.search) params.append('search', filters.search);
    appendCategoryFilters(params, filters);
    if (filters.groupId) params.append('groupId', filters.groupId);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.cursor) params.append('cursor', filters.cursor);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
    
    const response = await api.get(`/collections?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const listUsedCollections = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    appendCategoryFilters(params, filters);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.cursor) params.append('cursor', filters.cursor);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
    
    const response = await api.get(`/collections/used?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const listFavoriteCollections = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    appendCategoryFilters(params, filters);
    if (filters.groupId) params.append('groupId', filters.groupId);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.cursor) params.append('cursor', filters.cursor);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
    
    const response = await api.get(`/collections/favorites?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const createCollection = async (collectionData) => {
  try {
    const response = await api.post('/collections', collectionData);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const getCollection = async (id) => {
  try {
    const response = await api.get(`/collections/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const updateCollection = async (id, collectionData) => {
  try {
    const response = await api.put(`/collections/${id}`, collectionData);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const deleteCollection = async (id) => {
  try {
    const response = await api.delete(`/collections/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const attachPromptToCollection = async (collectionId, promptId) => {
  try {
    const response = await api.post(`/collections/${collectionId}/prompts`, { promptId });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const detachPromptFromCollection = async (collectionId, promptId) => {
  try {
    const response = await api.delete(`/collections/${collectionId}/prompts/${promptId}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const attachContextToCollection = async (collectionId, contextId, folderKey = null) => {
  try {
    const body = folderKey != null && String(folderKey).trim() !== '' ? { contextId, folderKey } : { contextId };
    const response = await api.post(`/collections/${collectionId}/contexts`, body);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const detachContextFromCollection = async (collectionId, contextId) => {
  try {
    const response = await api.delete(`/collections/${collectionId}/contexts/${contextId}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const cloneCollection = async (collectionId, payload = {}) => {
  try {
    const response = await api.post(`/collections/${collectionId}/clone`, payload);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const getCollectionComments = async (collectionId) => {
  try {
    const response = await api.get(`/collections/${collectionId}/comments`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const createCollectionComment = async (collectionId, content) => {
  try {
    const response = await api.post(`/collections/${collectionId}/comments`, { content });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const deleteCollectionComment = async (collectionId, commentId) => {
  try {
    const response = await api.delete(`/collections/${collectionId}/comments/${commentId}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const login = async (credentials) => {
  try {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const signup = async (userData) => {
  try {
    const response = await api.post('/auth/signup', userData);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const registerUser = async (userData) => {
  try {
    const response = await api.post('/auth/register', userData);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const verifyEmail = async (email, confirmationCode) => {
  try {
    const response = await api.post('/auth/verify-email', { email, confirmationCode });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const resendVerificationCode = async (email) => {
  try {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const forgotPassword = async (email) => {
  try {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const confirmPasswordReset = async (resetData) => {
  try {
    const response = await api.post('/auth/confirm-password-reset', resetData);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const refreshToken = async (refreshToken) => {
  try {
    const response = await api.post('/auth/refresh-token', { refreshToken });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const logout = async (accessToken) => {
  try {
    const response = await api.post('/auth/logout', { accessToken });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const getUserProfile = async () => {
  if (getUserProfileInFlight) {
    return getUserProfileInFlight;
  }
  const run = async () => {
    try {
      const response = await api.get('/auth/profile');
      return response.data;
    } catch (error) {
      const err = new Error(getErrorMessage(error));
      err.response = error.response;
      err.status = error.response?.status;
      throw err;
    } finally {
      getUserProfileInFlight = null;
    }
  };
  getUserProfileInFlight = run();
  return getUserProfileInFlight;
};

export const getGoogleDriveStatus = async () => {
  try {
    const response = await api.get('/auth/google-drive/status');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const startGoogleDriveConnect = async ({ origin, redirectPath }) => {
  try {
    const response = await api.post('/auth/google-drive/start', {
      origin,
      redirectPath
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const disconnectGoogleDrive = async () => {
  try {
    const response = await api.post('/auth/google-drive/disconnect');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const getUserProfileWithToken = async (token) => {
  try {
    const response = await axios.get(`${API_URL}/auth/profile`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    error.message = getErrorMessage(error);
    throw error;
  }
};

export const completeSocialSignup = async (profileData, token) => {
  try {
    const response = await axios.post(`${API_URL}/auth/social/complete`, profileData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    error.message = getErrorMessage(error);
    throw error;
  }
};

export const getAccountSettings = async () => {
  try {
    const response = await api.get('/auth/account-settings');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const updateAccountSettings = async (settings) => {
  try {
    const response = await api.put('/auth/account-settings', settings);
    emitSessionDataInvalidation('prompthub:invalidate-account-settings');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const purchaseTokens = async (companyId, tokenAmount) => {
  try {
    const response = await api.post('/auth/purchase-tokens', {
      companyId,
      tokenAmount
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const createCompanyWithOwner = async (companyData) => {
  try {
    const response = await api.post('/auth/create-company', companyData);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const updateUserProfile = async (profileData) => {
  try {
    const response = await api.put('/auth/profile', profileData);
    emitSessionDataInvalidation('prompthub:invalidate-profile');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// Interaction API functions
export const likePrompt = async (promptId) => {
  try {
    const response = await api.post('/interactions/like', {
      promptId,
      action: 'like'
    });
    emitInteractionOptimistic({ key: 'liked', id: promptId, add: true });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const unlikePrompt = async (promptId) => {
  try {
    const response = await api.post('/interactions/like', {
      promptId,
      action: 'unlike'
    });
    emitInteractionOptimistic({ key: 'liked', id: promptId, add: false });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const favoritePrompt = async (promptId) => {
  try {
    const response = await api.post('/interactions/favorite', {
      promptId,
      action: 'favorite'
    });
    emitInteractionOptimistic({ key: 'favorited', id: promptId, add: true });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const unfavoritePrompt = async (promptId) => {
  try {
    const response = await api.post('/interactions/favorite', {
      promptId,
      action: 'unfavorite'
    });
    emitInteractionOptimistic({ key: 'favorited', id: promptId, add: false });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const incrementUses = async (promptId, action) => {
  try {
    const response = await api.post('/interactions/uses', {
      promptId,
      action
    });
    window.dispatchEvent(new CustomEvent('promptcrew:capability-activity', { detail: response.data?.prompt }));
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const incrementCollectionUsage = async (collectionId) => {
  try {
    const response = await api.post('/interactions/collection/uses', {
      collectionId
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// Increment context's own usage stats (on the context itself)
export const incrementContextStats = async (contextId) => {
  try {
    const response = await api.post(`/contexts/${contextId}/increment-usage`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// Record user interaction with context (in USER_INTERACTIONS_TABLE)
export const incrementContextUsage = async (contextId) => {
  try {
    const response = await api.post('/interactions/context/uses', {
      contextId
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const decrementContextUsage = async (contextId) => {
  try {
    const response = await api.post('/interactions/context/decrement-uses', {
      contextId
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// Collection interaction API functions
export const likeCollection = async (collectionId) => {
  try {
    const response = await api.post('/interactions/collection/like', {
      collectionId,
      action: 'like'
    });
    emitInteractionOptimistic({ key: 'likedCollections', id: collectionId, add: true });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const unlikeCollection = async (collectionId) => {
  try {
    const response = await api.post('/interactions/collection/like', {
      collectionId,
      action: 'unlike'
    });
    emitInteractionOptimistic({ key: 'likedCollections', id: collectionId, add: false });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const favoriteCollection = async (collectionId) => {
  try {
    const response = await api.post('/interactions/collection/favorite', {
      collectionId,
      action: 'favorite'
    });
    emitInteractionOptimistic({ key: 'favoritedCollections', id: collectionId, add: true });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const unfavoriteCollection = async (collectionId) => {
  try {
    const response = await api.post('/interactions/collection/favorite', {
      collectionId,
      action: 'unfavorite'
    });
    emitInteractionOptimistic({ key: 'favoritedCollections', id: collectionId, add: false });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const getUserInteractions = async () => {
  try {
    const response = await api.get('/interactions/user');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// Share functions
export const sharePrompt = async (promptId) => {
  try {
    const response = await api.post('/prompts/share', {
      promptId
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const getSharedPrompt = async (shareToken) => {
  try {
    // Don't send auth token for shared prompts (public endpoint)
    const response = await axios.get(`${API_URL}/shared/${shareToken}`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    // Preserve the original error so we can check status code and response data
    const enhancedError = new Error(getErrorMessage(error));
    enhancedError.response = error.response;
    enhancedError.status = error.response?.status;
    throw enhancedError;
  }
};

export const getPublicCapability = async (capabilityId) => {
  try {
    const response = await axios.get(
      `${API_URL}/public/capabilities/${encodeURIComponent(capabilityId)}`,
      { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data;
  } catch (error) {
    const enhancedError = new Error(getErrorMessage(error));
    enhancedError.response = error.response;
    enhancedError.status = error.response?.status;
    throw enhancedError;
  }
};

export const getPromptById = async (promptId) => {
  try {
    const response = await api.get(`/prompts/${promptId}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// User Management API functions
export const listTeamMembers = async (teamId, companyId) => {
  try {
    const response = await api.get(`/auth/team-members?teamId=${teamId}&companyId=${companyId}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const listCompanyUsers = async (companyId, includeRemoved = false) => {
  try {
    const params = new URLSearchParams();
    params.append('companyId', companyId);
    if (includeRemoved) params.append('includeRemoved', 'true');
    
    const response = await api.get(`/auth/company/users?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const listGroups = async (companyId, status, userId) => {
  try {
    const params = new URLSearchParams();
    if (companyId) params.append('companyId', companyId);
    if (status) params.append('status', status);
    if (userId) params.append('userId', userId);
    
    const response = await api.get(`/auth/groups?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const listTeams = async (companyId, status) => {
  try {
    const params = new URLSearchParams();
    if (companyId) params.append('companyId', companyId);
    if (status) params.append('status', status);
    
    const response = await api.get(`/auth/teams?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const createTeam = async (teamData) => {
  try {
    const response = await api.post('/auth/teams', teamData);
    emitSessionDataInvalidation('prompthub:invalidate-groups');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const updateTeam = async (teamId, name, description, updatedBy, companyId) => {
  try {
    const response = await api.put(`/auth/teams/${teamId}`, {
      name,
      description,
      updatedBy,
      companyId
    });
    emitSessionDataInvalidation('prompthub:invalidate-groups');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const deleteTeam = async (teamId, deletedBy, companyId) => {
  try {
    const response = await api.delete(`/auth/teams/${teamId}`, {
      data: { deletedBy, companyId }
    });
    emitSessionDataInvalidation('prompthub:invalidate-groups');
    return response.data;
  } catch (error) {
    console.error('deleteTeam error:', error.response?.data || error.message);
    throw error.response?.data || error.message;
  }
};

export const createGroup = async (groupData) => {
  try {
    const response = await api.post('/auth/groups', groupData);
    emitSessionDataInvalidation('prompthub:invalidate-groups');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const updateGroup = async (groupId, name, description, updatedBy, companyId) => {
  try {
    const response = await api.put(`/auth/groups/${groupId}`, {
      name,
      description,
      updatedBy,
      companyId
    });
    emitSessionDataInvalidation('prompthub:invalidate-groups');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const deleteGroup = async (groupId, deletedBy, companyId) => {
  try {
    const response = await api.delete(`/auth/groups/${groupId}`, {
      data: { deletedBy, companyId }
    });
    emitSessionDataInvalidation('prompthub:invalidate-groups');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const updateUserRole = async (userId, role, updatedBy) => {
  try {
    const requestBody = { 
      userId, 
      newRole: role, 
      updatedBy 
    };
    const response = await api.put(`/auth/users/${userId}/role`, requestBody);
    return response.data;
  } catch (error) {
    console.error('updateUserRole error:', error.response?.data || error.message);
    throw error.response?.data || error.message;
  }
};

export const updateUserStatus = async (userId, status, updatedBy, companyId) => {
  try {
    const response = await api.put(`/auth/users/${userId}/status`, { 
      userId, 
      status, 
      updatedBy, 
      companyId 
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const updateUserName = async (userId, firstName, lastName, updatedBy, companyId) => {
  try {
    const response = await api.put(`/auth/users/${userId}/name`, { 
      userId, 
      firstName, 
      lastName, 
      updatedBy, 
      companyId 
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const removeTeamMember = async (userId, removedBy, companyId) => {
  try {
    const response = await api.delete(`/auth/users/${userId}/remove`, {
      data: { userId, removedBy, companyId }
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const addTeamMember = async (teamId, userId, companyId, addedBy) => {
  try {
    const response = await api.post(`/auth/teams/${teamId}/members`, {
      userId,
      teamId,
      companyId,
      addedBy
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const addMemberToGroup = async (groupId, userId, addedBy, companyId) => {
  try {
    const response = await api.post(`/auth/groups/${groupId}/members`, { 
      groupId,
      memberId: userId, // Backend expects 'memberId'
      addedBy,
      companyId
    });
    emitSessionDataInvalidation('prompthub:invalidate-groups');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const removeMemberFromGroup = async (groupId, userId, removedBy, companyId) => {
  try {
    const response = await api.delete(`/auth/groups/${groupId}/members`, { 
      data: { 
        memberId: userId,
        removedBy,
        companyId
      } 
    });
    emitSessionDataInvalidation('prompthub:invalidate-groups');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const addTeamToGroup = async (groupId, teamId, addedBy, companyId) => {
  try {
    const response = await api.post(`/auth/groups/${groupId}/teams`, { 
      groupId,
      teamId,
      addedBy,
      companyId
    });
    emitSessionDataInvalidation('prompthub:invalidate-groups');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const removeTeamFromGroup = async (groupId, teamId, removedBy, companyId) => {
  try {
    const response = await api.delete(`/auth/groups/${groupId}/teams`, { 
      data: { 
        teamId,
        removedBy,
        companyId
      } 
    });
    emitSessionDataInvalidation('prompthub:invalidate-groups');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const inviteUser = async (invitationData) => {
  try {
    const response = await api.post('/auth/invite', invitationData);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const getInvitationForSelf = async (invitationId) => {
  try {
    const response = await api.get(`/auth/invitations/${invitationId}/self`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const respondCompanyInvitation = async (invitationId, action) => {
  try {
    const response = await api.post(`/auth/invitations/${invitationId}/respond`, { action });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const listInvitations = async (companyId, status) => {
  try {
    const params = new URLSearchParams();
    if (companyId) params.append('companyId', companyId);
    if (status) params.append('status', status);
    
    const response = await api.get(`/auth/invitations?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const deleteInvitation = async (invitationId, deletedBy, companyId) => {
  try {
    const response = await api.delete(`/auth/invitations/${invitationId}`, {
      data: { deletedBy, companyId }
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const acceptInvitation = async (invitationData) => {
  try {
    const response = await api.post('/auth/accept-invitation', invitationData);
    return response.data;
  } catch (error) {
    console.error('acceptInvitation error:', error.response?.data || error.message);
    throw error.response?.data || error.message;
  }
};

export const deleteUser = async (userId, deletedBy, companyId) => {
  try {
    const response = await api.delete(`/auth/users/${userId}`, {
      data: { deletedBy, companyId }
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// Document parsing
export const parseDocument = async (fileContent, fileName, fileType) => {
  try {
    const response = await api.post('/documents/parse', {
      fileContent,
      fileName,
      fileType
    });
    return response.data;
  } catch (error) {
    console.error('Error parsing document:', error);
    throw error.response?.data || error.message;
  }
};

export const fetchWebContext = async (url) => {
  try {
    const response = await api.post('/documents/fetch-web', { url });
    return response.data;
  } catch (error) {
    console.error('Error fetching web context:', error);
    throw error.response?.data || error.message;
  }
};

/** Export markdown as DOCX file; returns blob and suggested filename for download. */
export const exportDocx = async (markdown, fileName = 'document.docx') => {
  const response = await api.post(
    '/documents/export-docx',
    { markdown, fileName },
    { responseType: 'arraybuffer' }
  );
  if (response.status < 200 || response.status >= 300) {
    const text = new TextDecoder('utf-8').decode(response.data);
    let msg = 'Export failed';
    try {
      const json = JSON.parse(text);
      if (json.message) msg = json.message;
    } catch (_) {}
    throw new Error(msg);
  }
  const contentType = (response.headers['content-type'] || '').toLowerCase();
  let bytes = new Uint8Array(response.data);

  // Some runtimes/proxies return Lambda base64 body as plain text instead of binary bytes.
  // Detect that payload and decode it so the downloaded file is a valid DOCX zip.
  const isZip = bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4b; // "PK"
  if (!isZip && !contentType.includes('application/json')) {
    const textPayload = new TextDecoder('utf-8').decode(bytes).trim();
    const maybeBase64 = textPayload.replace(/^"|"$/g, '').replace(/\s+/g, '');
    const looksLikeBase64Zip =
      maybeBase64.length > 8 &&
      maybeBase64.startsWith('UEsD') &&
      /^[A-Za-z0-9+/=]+$/.test(maybeBase64);

    if (looksLikeBase64Zip) {
      const bin = atob(maybeBase64);
      const decoded = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) {
        decoded[i] = bin.charCodeAt(i);
      }
      bytes = decoded;
    }
  }

  const finalIsZip = bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4b;
  if (!finalIsZip) {
    throw new Error('Export failed: generated file is not a valid DOCX.');
  }

  const disposition = response.headers['content-disposition'] || '';
  const match = disposition.match(/filename="?([^";\n]+)"?/);
  const name = match ? match[1].trim() : fileName;
  return {
    blob: new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    }),
    fileName: name
  };
};

// Context Library API functions
export const listContexts = async (scope = 'shared', filters = {}) => {
  try {
    const params = new URLSearchParams();
    params.append('scope', scope);
    if (filters.search) params.append('search', filters.search);
    appendCategoryFilters(params, filters);
    if (filters.collectionId) params.append('collectionId', filters.collectionId);
    if (filters.groupId) params.append('groupId', filters.groupId);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.cursor) params.append('cursor', filters.cursor);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
    
    const response = await api.get(`/contexts?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const listContextsByCollection = async (collectionId) => {
  try {
    const response = await api.get(`/contexts/collection/${collectionId}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const listUsedContexts = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    appendCategoryFilters(params, filters);
    if (filters.search) params.append('search', filters.search);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.cursor) params.append('cursor', filters.cursor);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);

    const response = await api.get(`/contexts/used?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const listFavoriteContexts = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    appendCategoryFilters(params, filters);
    if (filters.search) params.append('search', filters.search);
    if (filters.groupId) params.append('groupId', filters.groupId);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.cursor) params.append('cursor', filters.cursor);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
    
    const response = await api.get(`/contexts/favorites?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const getContext = async (id) => {
  try {
    const response = await api.get(`/contexts/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const createContext = async (contextData) => {
  try {
    const response = await api.post('/contexts', contextData);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// Feedback API functions
export const createFeedback = async (feedbackData) => {
  try {
    const response = await api.post('/feedback', feedbackData);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const updateContext = async (id, contextData) => {
  try {
    const response = await api.put(`/contexts/${id}`, contextData);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const deleteContext = async (id) => {
  try {
    const response = await api.delete(`/contexts/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// Context Interaction API functions
export const likeContext = async (contextId) => {
  try {
    const response = await api.post('/interactions/context/like', {
      contextId,
      action: 'like'
    });
    emitInteractionOptimistic({ key: 'likedContexts', id: contextId, add: true });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const unlikeContext = async (contextId) => {
  try {
    const response = await api.post('/interactions/context/like', {
      contextId,
      action: 'unlike'
    });
    emitInteractionOptimistic({ key: 'likedContexts', id: contextId, add: false });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const favoriteContext = async (contextId) => {
  try {
    const response = await api.post('/interactions/context/favorite', {
      contextId,
      action: 'favorite'
    });
    emitInteractionOptimistic({ key: 'favoritedContexts', id: contextId, add: true });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const unfavoriteContext = async (contextId) => {
  try {
    const response = await api.post('/interactions/context/favorite', {
      contextId,
      action: 'unfavorite'
    });
    emitInteractionOptimistic({ key: 'favoritedContexts', id: contextId, add: false });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const improvePrompt = async (promptText, options = {}) => {
  try {
    const body = { promptText };
    if (options.instruction) body.instruction = options.instruction;
    if (options.mode) body.mode = options.mode;
    if (options.capabilityType) body.capabilityType = options.capabilityType;
    const response = await api.post('/ai/prompt-improvement', body);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const executePrompt = async ({ model, messageHistory, contextIds, temporaryContexts, temperature, max_tokens, promptId, recordUsage = true }) => {
  try {
    const body = {
      model,
      messageHistory,
      temperature,
      max_tokens,
      ...(promptId && !String(promptId).startsWith('temp-') ? { promptId } : {}),
    };
    if (Array.isArray(contextIds) && contextIds.length > 0) {
      body.contextIds = contextIds;
    }
    if (Array.isArray(temporaryContexts) && temporaryContexts.length > 0) {
      body.temporaryContexts = temporaryContexts;
    }
    const response = await api.post('/ai/execute-prompt', body);
    if (body.promptId && recordUsage && response.data?.message?.content) {
      try {
        await incrementUses(body.promptId, 'execute');
      } catch (error) {
        // A tracking failure must not discard an already produced answer.
        console.warn('Could not record capability usage', error.message);
      }
    }
    window.dispatchEvent(new Event('promptcrew:activation-changed'));
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const getActivationProgress = async () => (await api.get('/onboarding/activation')).data;
export const updateActivationProgress = async (milestoneId, action) => {
  const result = await api.post('/onboarding/activation', { milestoneId, action });
  window.dispatchEvent(new Event('promptcrew:activation-changed'));
  return result.data;
};

export const buildPrompt = async (promptData) => {
  try {
    const response = await api.post('/ai/build-prompt', promptData);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const suggestContext = async (promptText, options = {}) => {
  try {
    const payload = { promptText };
    if (Array.isArray(options.attachedContexts) && options.attachedContexts.length > 0) {
      payload.attachedContexts = options.attachedContexts;
    }
    if (options.buildBrief && typeof options.buildBrief === 'object') {
      payload.buildBrief = options.buildBrief;
    }
    const response = await api.post('/ai/suggest-context', payload);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const assistantDiscover = async (payload = {}) => {
  try {
    const response = await api.post('/ai/assistant/discover', payload);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const inferCreateFlowSeed = async (payload = {}) => {
  try {
    const response = await api.post('/ai/infer-create-flow-seed', payload);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const suggestPromptVariables = async (promptText) => {
  try {
    const response = await api.post('/ai/suggest-prompt-variables', { promptText });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const generateMetadata = async (promptText, categories = [], usageHints = null) => {
  try {
    const body = { promptText, categories };
    if (usageHints != null && usageHints !== '') {
      body.usageHints = usageHints;
    }
    const response = await api.post('/ai/generate-metadata', body);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const generateContextMetadata = async (content) => {
  try {
    const response = await api.post('/ai/generate-context-metadata', { content });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const getAdminAnalytics = async (params = {}) => {
  try {
    const response = await api.get('/admin/analytics', { params });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const getModelMultipliers = async () => {
  try {
    const response = await api.get('/admin/model-multipliers');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const updateModelMultipliers = async (modelMultipliers = {}) => {
  try {
    const response = await api.put('/admin/model-multipliers', { modelMultipliers });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const getBillingOverview = async () => {
  try {
    const response = await api.get('/admin/billing/overview');
    const data = response.data || {};
    const normalizedPlan = (data.plan || '').toUpperCase() === 'FREEMIUM' ? 'FREE' : data.plan;
    return { ...data, plan: normalizedPlan };
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const getBillingLicenses = async () => {
  try {
    const response = await api.get('/admin/billing/licenses');
    const data = response.data || {};
    const normalizedTier = (data.subscriptionTier || '').toUpperCase() === 'FREEMIUM' ? 'FREE' : data.subscriptionTier;
    const users = Array.isArray(data.users)
      ? data.users.map((u) => {
          if (!u.licenseAssignment) return { ...u, licenseAssignment: null };
          const raw = (u.licenseAssignment.tier || '').toString();
          const tier = raw.toUpperCase() === 'FREEMIUM' ? 'FREE' : raw;
          return { ...u, licenseAssignment: { ...u.licenseAssignment, tier } };
        })
      : [];
    return { ...data, subscriptionTier: normalizedTier, users };
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const assignBillingLicense = async (userId, tier) => {
  try {
    const response = await api.put('/admin/billing/licenses/assign', { userId, tier });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const unassignBillingLicense = async (userId) => {
  try {
    const response = await api.put('/admin/billing/licenses/unassign', { userId });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const getInvoices = async () => {
  try {
    const response = await api.get('/admin/billing/invoices');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const getPaymentMethod = async () => {
  try {
    const response = await api.get('/admin/billing/payment-method');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const createBillingCheckoutSession = async ({ basicSeats, proSeats }) => {
  try {
    const response = await api.post('/admin/billing/checkout-session', { basicSeats, proSeats });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const reconcileBillingCheckoutSession = async (checkoutSessionId) => {
  try {
    const response = await api.post('/admin/billing/checkout-session/reconcile', { checkoutSessionId });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const createBillingPortalSession = async () => {
  try {
    const response = await api.post('/admin/billing/portal-session');
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const updateBillingSeatQuantities = async ({ basicSeats, proSeats }) => {
  try {
    const response = await api.post('/admin/billing/licenses/update', { basicSeats, proSeats });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};
